import { Loader2 } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { asValidationOutcome, validationPresentation } from "@/lib/studio/validation";
import type { RunEvent, ValidationOutcome } from "@/services/studio/types";

export type StageStatus = "pending" | "running" | "done" | "warning" | "error";

/**
 * The org-validation verdict carried on its `step_result` event.
 *
 * That step reports THREE outcomes but its `ok` flag is `outcome === "validated"`
 * (playground `runner/execution.py`), so a check that never COMPLETED arrives as
 * `ok: false` — byte-identical to one the org rejected. Reading `ok` alone paints
 * `unvalidated` red and tells the user their file is broken when it was simply
 * never checked. Read the verdict off the event payload instead, and treat an
 * unrecognised outcome as `unvalidated`: absence of a pass is not a pass.
 */
export function orgValidationOutcome(event: RunEvent): ValidationOutcome | null {
  if (event.tool !== "org_validation") return null;
  const data = event.data as { outcome?: unknown } | null | undefined;
  return asValidationOutcome(data?.outcome) ?? "unvalidated";
}

/** Three-state status for a `step_result`, so `unvalidated` is neither done nor error. */
function stepResultStatus(event: RunEvent): StageStatus {
  const outcome = orgValidationOutcome(event);
  if (outcome) {
    return outcome === "validated" ? "done" : outcome === "validation_failed" ? "error" : "warning";
  }
  return event.ok === false ? "error" : "done";
}

/** Label for a `step_result`, honouring the three-state org-validation verdict. */
function stepResultLabel(event: RunEvent, tool: string): string {
  const outcome = orgValidationOutcome(event);
  if (outcome) return validationPresentation(outcome).label;
  return stageDoneLabelForTool(tool, event.ok !== false);
}

export interface RunProgress {
  completed: number;
  total: number;
  percent: number;
  /**
   * True when the backend never declared how many steps the run has, so any
   * percentage would be invented. Callers must render a step count and an
   * indeterminate bar instead of a number.
   */
  indeterminate: boolean;
}

/**
 * Read a declared step total off an event. `total_steps` rides on `started`, `total` on
 * step events. Both are absent (NOT null, NOT 0) when the backend genuinely does not
 * know the count — a reconnect to a still-running run, or the reactive loop whose hop
 * count is dynamic. Anything non-positive is treated as "not declared".
 */
function declaredTotalFrom(event: RunEvent): number {
  const raw = typeof event.total_steps === "number" ? event.total_steps : event.total;
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : 0;
}

export function progressFromEvents(events: RunEvent[]): RunProgress {
  const completed = events.filter((event) => event.event === "step_result").length;
  const declaredTotal = events.reduce((max, event) => {
    const value = declaredTotalFrom(event);
    return value > max ? value : max;
  }, 0);
  // The backend signals run completion explicitly via a terminal `done` event
  // (which carries progress:1). Honor it: without this the bar freezes at
  // completed/total — e.g. one LLM step of five declared phases = 20% — and
  // never reaches 100% even though the run finished.
  if (events.some((event) => event.event === "done")) {
    const resolved = Math.max(declaredTotal, completed, 1);
    return { completed: resolved, total: resolved, percent: 100, indeterminate: false };
  }
  // No declared total: the run is in progress and we do NOT know how many steps it
  // has. The old code invented a `completed + 1` denominator, which showed a
  // confident "1 of 2 · 50%" for a total nobody ever reported. Report the honest
  // step count and let the caller render an indeterminate bar.
  if (declaredTotal <= 0) {
    return { completed, total: 0, percent: 0, indeterminate: true };
  }
  const total = Math.max(declaredTotal, completed);
  return {
    completed,
    total,
    percent: Math.min(100, Math.round((completed / total) * 100)),
    indeterminate: false,
  };
}

export function eventLabel(event: RunEvent) {
  const label = typeof event.label === "string" && event.label.trim()
    ? event.label
    : typeof event.stage === "string" && event.stage.trim()
      ? event.stage
      : "";
  if (event.event === "started") return label || "Thinking through request";
  if (event.event === "step_start") return `${label || stageLabelForTool(String(event.tool ?? event.step_id ?? ""))}: ${event.tool ?? event.step_id ?? "step"}`;
  if (event.event === "step_progress") return label || `Working on ${event.tool ?? event.step_id ?? "step"}…`;
  if (event.event === "step_result") {
    return `${label || stepResultLabel(event, String(event.tool ?? event.step_id ?? ""))}: ${event.tool ?? event.step_id ?? "step"}`;
  }
  if (event.event === "artifact") return `${label || "Saving artifacts"}: ${event.name ?? "artifact"}`;
  if (event.event === "render") return label || "Preparing report preview";
  if (event.event === "done") return label || "Final report and artifacts ready";
  if (event.event === "error") return String(event.message ?? "Run failed");
  return event.event;
}

export function compactStageRows(events: RunEvent[]): Array<{ stage: string; tools: string[]; status: StageStatus; error?: string }> {
  const rows: Array<{ stage: string; tools: string[]; status: StageStatus; error?: string }> = [];
  for (const event of events) {
    if (event.event !== "step_start" && event.event !== "step_progress" && event.event !== "step_result" && event.event !== "artifact" && event.event !== "render" && event.event !== "done") {
      continue;
    }
    const tool = String(event.tool ?? event.step_id ?? "");
    const stage =
      typeof event.label === "string" && event.label.trim()
        ? event.label
        : typeof event.stage === "string" && event.stage.trim()
          ? event.stage
          : event.event === "artifact"
            ? "Saving artifacts"
            : event.event === "render"
              ? "Preparing report preview"
              : event.event === "done"
                ? "Final report and artifacts ready"
                : event.event === "step_result"
                  ? stepResultLabel(event, tool)
                  : stageLabelForTool(tool);
    const status: StageStatus =
      event.event === "step_start" || event.event === "step_progress"
        ? "running"
        : event.event === "step_result"
          ? stepResultStatus(event)
          : event.ok === false
            ? "error"
            : "done";
    // Normalise key so heartbeat updates like "LLM generating (5s)", "(10s)"…
    // collapse onto the same row instead of creating a new card each time.
    const stageKey = stage.replace(/\s*\(\d+s\)/g, "").split(" · ")[0].trim().toLowerCase();
    const existing = rows.find((row) => {
      const rowKey = row.stage.replace(/\s*\(\d+s\)/g, "").split(" · ")[0].trim().toLowerCase();
      return rowKey === stageKey;
    });
    if (existing) {
      // Always keep the latest label so the displayed timing stays current
      existing.stage = stage;
      if (tool && !existing.tools.includes(tool)) existing.tools.push(tool);
      existing.status = mergeStageStatus(existing.status, status);
      existing.error = existing.error || String(event.error ?? event.message ?? "");
      continue;
    }
    rows.push({
      stage,
      tools: tool ? [tool] : [],
      status,
      error: String(event.error ?? event.message ?? ""),
    });
  }
  // On run completion, any step still showing as running never got its own
  // terminal event — flip it to done so the checklist doesn't sit "pending"
  // forever after the run has actually finished. (Errors are left as-is.)
  if (events.some((event) => event.event === "done")) {
    for (const row of rows) {
      if (row.status === "running" || row.status === "pending") row.status = "done";
    }
  }
  return rows;
}

export function mergeStageStatus(left: StageStatus, right: StageStatus): StageStatus {
  if (left === "error" || right === "error") return "error";
  if (left === "warning" || right === "warning") return "warning";
  if (left === "running" || right === "running") return "running";
  if (left === "done" || right === "done") return "done";
  return "pending";
}

export function stageBadge(status: StageStatus) {
  if (status === "running") return <Badge variant="warm">Working</Badge>;
  if (status === "done") return <Badge variant="success">Done</Badge>;
  if (status === "error") return <Badge variant="destructive">Issue</Badge>;
  // Caution, not failure: the step ran but could not confirm an outcome (an org
  // validation that never completed). "Skipped" would claim it never ran at all.
  if (status === "warning") return <Badge variant="warm">Unconfirmed</Badge>;
  return <Badge variant="secondary">Waiting</Badge>;
}

export function stageLabelForTool(tool: string): string {
  const lower = tool.toLowerCase();
  if (!lower) return "Thinking";
  if (lower.includes("llm") || lower.includes("generate")) return "Generating output";
  if (lower.includes("validate") || lower.includes("test")) return "Validating result";
  if (lower.includes("persist") || lower.includes("artifact")) return "Saving artifacts";
  if (lower.includes("graph")) return "Mapping dependencies";
  if (lower.includes("source") || lower.includes("metadata") || lower.includes("class") || lower.includes("field") || lower.includes("object")) return "Reading metadata";
  if (lower.includes("query") || lower.includes("search")) return "Gathering evidence";
  return "Working";
}

export function stageDoneLabelForTool(tool: string, ok: boolean): string {
  if (!ok) return "Needs attention";
  const label = stageLabelForTool(tool);
  if (label === "Generating output") return "Generated output";
  if (label === "Validating result") return "Validated result";
  if (label === "Saving artifacts") return "Saved artifacts";
  if (label === "Mapping dependencies") return "Mapped dependencies";
  if (label === "Reading metadata") return "Read metadata";
  if (label === "Gathering evidence") return "Gathered evidence";
  if (label === "Thinking") return "Planned run";
  return "Completed step";
}

export function AgentRunProgressStrip({
  progress,
  status,
  streamLabel,
  events,
}: {
  progress: ReturnType<typeof progressFromEvents>;
  status: "idle" | "running" | "done" | "error" | "cancelled";
  streamLabel: string;
  events: RunEvent[];
}) {
  const lastEvent = events[events.length - 1];
  const lastLabel = lastEvent ? eventLabel(lastEvent) : "Ready to run";
  const badgeVariant =
    status === "running" ? "success" : events.length > 0 ? "outline" : "secondary";

  return (
    <div className="rounded-md border border-border bg-card/70 px-4 py-3 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Badge variant={badgeVariant}>{streamLabel}</Badge>
          <span className="truncate font-mono text-xs text-muted-foreground">
            {lastLabel}
          </span>
        </div>
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {progress.indeterminate
            ? `${progress.completed} ${progress.completed === 1 ? "step" : "steps"} complete`
            : `${progress.completed} of ${progress.total} complete · ${progress.percent}%`}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        {/* No declared total — an animated sliver that travels, rather than a width
            derived from a denominator the backend never sent. */}
        {progress.indeterminate ? (
          <div className="h-full w-1/3 animate-[pulse_1.6s_ease-in-out_infinite] rounded-full bg-primary/60" />
        ) : (
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progress.percent}%` }}
          />
        )}
      </div>
    </div>
  );
}

export function AgentExecutionGraph({
  events,
  emptyLabel = "Run the agent to see progress.",
}: {
  events: RunEvent[];
  emptyLabel?: string;
}) {
  const rows = compactStageRows(events);
  if (rows.length === 0) {
    return <p className="font-mono text-xs text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <ol className="divide-y divide-border/40">
      {rows.map((row, idx) => (
        <li key={`${row.stage}-${idx}`} className="flex items-center gap-2 py-1.5">
          <span className="w-5 shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground/50">
            {idx + 1}.
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-mono text-xs text-foreground">{row.stage}</span>
              {row.tools.length > 0 && (
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground/50">
                  {row.tools[0]}{row.tools.length > 1 ? ` +${row.tools.length - 1}` : ""}
                </span>
              )}
            </div>
            {row.error && (
              <p className="mt-0.5 line-clamp-1 font-mono text-[10px] text-destructive">{row.error}</p>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-1">
            {row.status === "running" && (
              <Loader2 className="h-3 w-3 animate-spin text-[hsl(var(--accent-warm))]" />
            )}
            {stageBadge(row.status)}
          </div>
        </li>
      ))}
    </ol>
  );
}
